import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { clsx } from 'clsx';

type Props = { children: React.ReactNode; noPaddingTop?: boolean };

export function TopSection({ children }: Props) {
  return <div className="flex h-[150px] flex-col border-b border-gray-200 px-6">{children}</div>;
}

export function PageSection({ children }: Props) {
  return <div className="flex w-full">{children}</div>;
}

export function PageContentSection({ children, noPaddingTop }: Props) {
  return (
    <div
      className={clsx(
        'flex h-[calc(100vh-150px)] w-full flex-col justify-between gap-4 overflow-y-auto px-5 pb-6',
        noPaddingTop ? 'pt-0' : 'pt-6'
      )}>
      {children}
    </div>
  );
}

type Section = {
  name: string;
  header: string;
  icon: IconDefinition;
  render: (setExpanded: (expanded: string) => void) => React.ReactNode;
  showNotificationDot?: boolean;
};

type ExpandableSidebarSectionProps = {
  sections: Section[];
  expandedSection?: string | null;
  onExpandedSectionChange?: (expanded: string | null) => void;
};

export function ExpandableSidebarSection({
  sections,
  expandedSection: controlledExpanded,
  onExpandedSectionChange
}: ExpandableSidebarSectionProps) {
  const [internalExpanded, setInternalExpanded] = React.useState<string | null>(() => {
    // roughly how many pixels before the center viewport begins to shrink past content width
    if (typeof window !== 'undefined' && window.innerWidth < 1393) {
      return null;
    }
    return sections[0]?.name || null;
  });

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const setExpanded = (value: string | null) => {
    if (!isControlled) {
      setInternalExpanded(value);
    }
    onExpandedSectionChange?.(value);
  };

  function setExpandedSection(sectionName: string) {
    if (!sections.map(s => s.name).includes(sectionName)) {
      console.error(`Section ${sectionName} not found`);
      return;
    }
    if (expanded === sectionName) setExpanded(null);
    else setExpanded(sectionName);
  }

  return (
    <>
      <div className={clsx('shrink-0 transition-all duration-500', expanded ? 'w-96' : 'w-0')}>
        {sections.map(({ name, header, render }) => (
          <RightSidePanel
            key={name}
            expanded={expanded === name}
            header={header}
            close={() => setExpanded(null)}>
            {render(setExpandedSection)}
          </RightSidePanel>
        ))}
      </div>
      <div className="z-20 flex w-14 shrink-0 flex-col items-center gap-2 border-l border-gray-200 bg-white px-2 py-6">
        {sections.map(({ name, icon, showNotificationDot }) => (
          <div
            key={name}
            className={clsx('relative p-2', expanded === name && 'rounded-md bg-blue-100')}>
            <FontAwesomeIcon
              className="h-5 w-5 cursor-pointer"
              icon={icon}
              onClick={() => setExpandedSection(name)}
            />
            {showNotificationDot && (
              <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
            )}
          </div>
        ))}
      </div>
    </>
  );
}

type RightSidePanelProps = {
  expanded: boolean;
  header: string;
  close: () => unknown;
  children: React.ReactNode;
};

function RightSidePanel({ expanded, header, close, children }: RightSidePanelProps) {
  return (
    <div
      className={clsx(
        'fixed bottom-0 z-10 flex h-[calc(100vh-150px)] w-96 bg-white transition-all duration-500',
        expanded ? 'right-[56px] border-l border-gray-200' : 'right-[-384px]'
      )}>
      <div className="w-full pt-4">
        <div className="flex items-center">
          <div className="ml-4 text-xl font-semibold">{header}</div>
        </div>
        <div className="ml-[-16px] flex w-full items-center">
          <div
            className="flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white"
            onClick={close}>
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </div>
        <div className="mt-[-16px] h-[calc(100vh-210px)] overflow-y-scroll py-0 pb-4 pl-[18px] pr-4">
          {children}
        </div>
      </div>
    </div>
  );
}
